import { getSector } from '@utils/map/sector.utils';
import {
  getNotSelectedSectorConfiguration,
  getSelectedSectorConfiguration,
  isJamming,
} from '@utils/sensors/neutral/neutral.utils';
import { Feature } from 'geojson';

import { Coordinates } from '@/types/commons/commons.types';
import { FeatureTypeEnum } from '@/types/map.types';
import { NeutralActiveMonitoringWithSectors } from '@/types/sensor/monitoring.types';
import { SectorConfig } from '@/types/sensor/sensors';

const toSectorCoverageFeatures = ({
  configuration,
  position,
  isJamming,
  isSelected,
  color,
}: {
  configuration: SectorConfig;
  position: Coordinates;
  isJamming: boolean;
  isSelected: boolean;
  color: string | null;
}): Feature[] => {
  const bearing1 = configuration.orientation - configuration.aperture / 2;
  const bearing2 = configuration.orientation + configuration.aperture / 2;

  const sectorCoverage = getSector(position, bearing1, bearing2, configuration.range);

  if (!sectorCoverage) {
    return [];
  }

  return [
    {
      type: 'Feature',
      id: `${FeatureTypeEnum.NEUTRAL_SECTOR}-${configuration.name}`,
      properties: {
        featureType: FeatureTypeEnum.NEUTRAL_SECTOR,
        value: configuration,
        isJamming,
        isSelected,
        color,
      },
      geometry: {
        type: 'Polygon',
        coordinates: sectorCoverage.geometry.coordinates,
      },
    },
  ];
};

export const toNeutralSectorCoverageFeatures = (monitoringSensors: NeutralActiveMonitoringWithSectors[]): Feature[] =>
  monitoringSensors
    .flatMap((monitoringSensor) => {
      const { configuration, status } = monitoringSensor;
      const selectedSectorConfiguration = getSelectedSectorConfiguration(monitoringSensor);
      const position = configuration.sensorPosition;
      const color = configuration.sensorColor;

      return getNotSelectedSectorConfiguration(monitoringSensor)
        .filter((sector) => sector.active)
        .map((configuration) => ({
          configuration,
          position,
          isJamming: false,
          isSelected: false,
          color,
        }))
        .concat(
          selectedSectorConfiguration
            ? [
                {
                  configuration: selectedSectorConfiguration,
                  position,
                  isJamming: isJamming(status),
                  isSelected: true,
                  color,
                },
              ]
            : [],
        );
    })
    .flatMap(toSectorCoverageFeatures);
